import { createSelector } from '@reduxjs/toolkit';

import type { CertificadoGerado } from '@/features/arbitral-detalhes';
import type { RootState } from '@/store';

import type {
  PainelAndamento,
  PainelDocumento,
  PainelPendencia,
  RespostaPendenciaPublicada,
} from '../types';

export const selectModelosEmissao = (state: RootState) => state.arbitro.modelosEmissao;

// TODO(mock-cutover): processos mock (seed/serviço/selectors) só serve telas ainda não
// migradas (DadosProcessoPanel/acaoPendente via tipo ProcessoArbitro); remover no cutover.
export const selectProcessosArbitro = (state: RootState) => state.arbitro.processos;
export const selectArbitroProcessosLoading = (state: RootState) => state.arbitro.isLoadingProcessos;
export const selectArbitroProcessosError = (state: RootState) => state.arbitro.errorProcessos;

// Painel inicial (dashboard server-driven).
export const selectDashboard = (state: RootState) => state.arbitro.dashboard;
export const selectDashboardLoading = (state: RootState) => state.arbitro.isLoadingDashboard;
export const selectDashboardError = (state: RootState) => state.arbitro.errorDashboard;

// Central/sino de notificações (server-driven).
export const selectNotificacoesPainel = (state: RootState) => state.arbitro.notificacoesPainel;
export const selectNotificacoesPainelLoading = (state: RootState) =>
  state.arbitro.isLoadingNotificacoesPainel;

// Contadores por aba derivados do feed (mesma semântica do backend). Resolvidos por último.
export const selectContadoresNotificacoes = createSelector([selectNotificacoesPainel], (itens) => ({
  aResolver: itens.filter((item) => !item.resolvido).length,
  pendencias: itens.filter((item) => item.acionavel && !item.resolvido).length,
  todas: itens.length,
  resolvidas: itens.filter((item) => item.resolvido).length,
}));

// Avisos ainda não lidos (habilita "marcar avisos como lidos").
export const selectAvisosNaoLidos = createSelector(
  [selectNotificacoesPainel],
  (itens) => itens.filter((item) => item.natureza === 'AVISO' && !item.resolvido).length,
);

export const selectProcessoArbitroPorId = (id: string) => (state: RootState) =>
  state.arbitro.processos.find((processo) => processo.id === id) ?? null;

export const selectPainelItems = (state: RootState) => state.arbitro.painelItems;
export const selectPainelPaginacao = (state: RootState) => state.arbitro.painelPaginacao;
export const selectPainelFacetas = (state: RootState) => state.arbitro.painelFacetas;
export const selectPainelLoading = (state: RootState) => state.arbitro.isLoadingPainel;
export const selectPainelError = (state: RootState) => state.arbitro.errorPainel;

export const selectPainelDetalhe = (state: RootState) => state.arbitro.detalheItem;
export const selectPainelDetalheLoading = (state: RootState) => state.arbitro.isLoadingDetalhe;
export const selectPainelDetalheError = (state: RootState) => state.arbitro.errorDetalhe;
export const selectPainelDetalheNaoEncontrado = (state: RootState) =>
  state.arbitro.detalheNaoEncontrado;

// A lista só vale para o processo de que ela veio. Ao trocar de processo, o detalhe do novo
// pode chegar antes da lista dele — e a tela renderizaria a timeline do anterior ao lado do
// badge já correto, que é o desencontro que este painel existe para não ter. Enquanto a
// proveniência não casa, a lista é vazia e a aba mostra o estado de carregamento.
const SEM_ANDAMENTOS: PainelAndamento[] = [];

export const selectPainelAndamentosDoProcesso = (processoId: string) => (state: RootState) =>
  state.arbitro.andamentosProcessoId === processoId ? state.arbitro.andamentos : SEM_ANDAMENTOS;

export const selectPainelAndamentosDoProcessoCarregados =
  (processoId: string) => (state: RootState) =>
    state.arbitro.andamentosProcessoId === processoId;

export const selectPainelAndamentosCarregando = (processoId: string) => (state: RootState) =>
  state.arbitro.isLoadingAndamentos || state.arbitro.andamentosProcessoId !== processoId;

export const selectPainelAndamentosError = (state: RootState) => state.arbitro.errorAndamentos;
export const selectPainelCriandoAndamento = (state: RootState) => state.arbitro.isCriandoAndamento;
export const selectPainelCriarAndamentoError = (state: RootState) =>
  state.arbitro.errorCriarAndamento;

export const selectPainelTermosPendentes = (state: RootState) => state.arbitro.termosPendentes;
export const selectPainelLoadingTermosPendentes = (state: RootState) =>
  state.arbitro.isLoadingTermosPendentes;
export const selectPainelEmitindoLote = (state: RootState) => state.arbitro.isEmitindoLote;

export const selectPainelEmitindoTermo = (state: RootState) => state.arbitro.isEmitindoTermo;
export const selectPainelErrorEmitirTermo = (state: RootState) => state.arbitro.errorEmitirTermo;

const SEM_PENDENCIAS: PainelPendencia[] = [];
const SEM_RESPOSTAS: Record<string, RespostaPendenciaPublicada> = {};

export const selectPainelPendencias = (id: string) =>
  createSelector(
    [
      (state: RootState) =>
        state.arbitro.pendenciasProcessoId === id ? state.arbitro.pendencias : SEM_PENDENCIAS,
      (state: RootState) => state.arbitro.respostasPendencias[id] ?? SEM_RESPOSTAS,
    ],
    (pendencias, respostas) =>
      pendencias.filter((pendencia) => !respostas[pendencia.id]?.resolvida),
  );
export const selectPainelLoadingPendencias = (id: string) => (state: RootState) =>
  state.arbitro.isLoadingPendencias || state.arbitro.pendenciasProcessoId !== id;
export const selectPainelPendenciasError = (id: string) => (state: RootState) =>
  state.arbitro.pendenciasProcessoId === id ? state.arbitro.errorPendencias : null;
export const selectRespostasPendencias = (id: string) => (state: RootState) =>
  state.arbitro.respostasPendencias[id] ?? SEM_RESPOSTAS;

export const selectFinanceiroResumo = (state: RootState) => state.arbitro.financeiroResumo;
export const selectFinanceiroItens = (state: RootState) => state.arbitro.financeiroItens;
export const selectFinanceiroLoading = (state: RootState) => state.arbitro.isLoadingFinanceiro;

const SEM_DOCUMENTOS: PainelDocumento[] = [];

export const selectPainelDocumentosDoProcesso = (processoId: string) => (state: RootState) =>
  state.arbitro.documentosProcessoId === processoId ? state.arbitro.documentos : SEM_DOCUMENTOS;

export const selectPainelDocumentosCarregando = (processoId: string) => (state: RootState) =>
  state.arbitro.isLoadingDocumentos || state.arbitro.documentosProcessoId !== processoId;

export const selectPainelDocumentosError = (state: RootState) => state.arbitro.errorDocumentos;

const SEM_CERTIFICADOS: CertificadoGerado[] = [];

export const selectPainelCertificadosDoProcesso = (processoId: string) => (state: RootState) =>
  state.arbitro.certificadosProcessoId === processoId
    ? state.arbitro.certificados
    : SEM_CERTIFICADOS;

export const selectPainelCertificadosCarregando = (processoId: string) => (state: RootState) =>
  state.arbitro.isLoadingCertificados || state.arbitro.certificadosProcessoId !== processoId;

export const selectPainelCertificadosError = (state: RootState) => state.arbitro.errorCertificados;

export const selectPainelCertificadoAcessos = (state: RootState) =>
  state.arbitro.certificadoAcessos;
export const selectPainelCertificadoAcessosCarregando = (state: RootState) =>
  state.arbitro.isLoadingCertificadoAcessos;
export const selectPainelCertificadoAcessosError = (state: RootState) =>
  state.arbitro.errorCertificadoAcessos;
